// Built-in copy for every CMS field, keyed by page and field (the field list itself is defined by the backend in
// backend/services/site_content.py). The website shows these unless an admin has published a change, so the site
// still renders in full when the content API is unreachable. They are read from the same data files the
// components use, so there is one copy of each text.
import { HOME } from '../data/home';
import { CONTACT_DETAILS, CONTACT_PAGE } from '../data/contact';
import { products } from '../config/products';
import { CLOUD_PBX } from '../data/cloudPbx';
import { SIP_TRUNK } from '../data/sipTrunk';
import { AI_RECEPTIONIST } from '../data/aiReceptionist';
import { SOFTPHONE } from '../data/softphone';
import { TEAMS_PHONE } from '../data/teamsPhone';
import { NUMBER_PAGES } from '../data/numbers';
import { VOICE_OTP } from '../data/voiceOtp';

export const HOME_PRODUCTS = products.filter((p) => p.home !== false);

const productDefaults = (D, { trust = true } = {}) => {
  const out = {
    'hero.headline': D.hero.title,
    'hero.highlight': D.hero.highlight || '',
    'hero.after': (D.hero.after || '').trim(),
    'hero.sub': D.hero.sub,
    'hero.bullets': D.hero.bullets || [],
    'intro.title': D.intro.title,
    'intro.paragraphs': D.intro.paragraphs,
    'intro.bullets': D.intro.bullets || [],
  };
  if (trust) {
    (D.trust || []).forEach((t, i) => {
      out[`stats.${i + 1}.value`] = t.value;
      out[`stats.${i + 1}.label`] = t.label;
    });
  }
  return out;
};

const home = {
  'hero.headline': HOME.hero.headline,
  'hero.highlight': HOME.hero.highlight,
  'hero.sub': HOME.hero.sub,
  'hero.primary_label': HOME.hero.primary.label,
  'hero.primary_url': HOME.hero.primary.to,
  'hero.secondary_label': HOME.hero.secondary.label,
  'hero.secondary_url': HOME.hero.secondary.to,
  'hero.image': HOME.hero.image,
  'hero.video': HOME.hero.video,
  'products.title': HOME.products.title,
  'products.subtitle': HOME.products.subtitle,
  'platform.title': HOME.platform.title,
  'platform.body': HOME.platform.body,
  'platform.bullet_1': HOME.platform.bullets[0],
  'platform.bullet_2': HOME.platform.bullets[1],
  'platform.bullet_3': HOME.platform.bullets[2],
  'platform.link_label': HOME.platform.linkLabel,
  'usecases.title': HOME.useCases.title,
  'usecases.subtitle': HOME.useCases.subtitle,
  'steps.title': HOME.steps.title,
  'cta.title': HOME.cta.title,
  'cta.subtitle': HOME.cta.subtitle,
  'cta.primary_label': HOME.cta.primaryLabel,
  'cta.secondary_label': HOME.cta.secondaryLabel,
};
HOME_PRODUCTS.forEach((p) => {
  home[`products.${p.slug}.title`] = p.title;
  home[`products.${p.slug}.description`] = p.description;
});
HOME.useCases.items.forEach((u, i) => {
  home[`usecases.${i + 1}.title`] = u.title;
  home[`usecases.${i + 1}.tag`] = u.tag;
  home[`usecases.${i + 1}.body`] = u.body;
});
HOME.steps.items.forEach((s, i) => {
  home[`steps.${i + 1}.verb`] = s.verb;
  home[`steps.${i + 1}.body`] = s.body;
});
for (let i = 1; i <= HOME.logoSlots; i += 1) {
  home[`logos.${i}.name`] = '';
  home[`logos.${i}.image`] = '';
}

export const SITE_CONTENT_DEFAULTS = {
  global: {
    'contact.email': CONTACT_DETAILS.email,
    'contact.phone': CONTACT_DETAILS.phone,
    'contact.address_street': CONTACT_DETAILS.addressStreet,
    'contact.address_locality': CONTACT_DETAILS.addressLocality,
    'contact.address_country': CONTACT_DETAILS.addressCountry,
    'footer.blurb': CONTACT_DETAILS.footerBlurb,
  },
  home,
  contact: {
    'hero.title': CONTACT_PAGE.title,
    'hero.sub': CONTACT_PAGE.sub,
    'hours.weekdays': CONTACT_PAGE.hours.weekdays,
    'hours.saturday': CONTACT_PAGE.hours.saturday,
    'hours.sunday': CONTACT_PAGE.hours.sunday,
    'hours.note': CONTACT_PAGE.hours.note,
  },
  'cloud-pbx': productDefaults(CLOUD_PBX),
  'sip-trunking': productDefaults(SIP_TRUNK),
  'ai-receptionist': productDefaults(AI_RECEPTIONIST),
  softphone: productDefaults(SOFTPHONE, { trust: false }),
  // The Teams key stats include a price, which is managed with the plans rather than here.
  'teams-phone': productDefaults(TEAMS_PHONE, { trust: false }),
  'voice-otp': {
    'hero.headline': VOICE_OTP.hero.title,
    'hero.highlight': VOICE_OTP.hero.highlight,
    'hero.sub': VOICE_OTP.hero.sub,
    'intro.title': VOICE_OTP.intro.title,
    'intro.body': VOICE_OTP.intro.body,
  },
  'numbers-did': productDefaults(NUMBER_PAGES.did),
  'numbers-toll-free': productDefaults(NUMBER_PAGES.tollfree),
  'numbers-mobile': productDefaults(NUMBER_PAGES.mobile),
  'numbers-2way': productDefaults(NUMBER_PAGES.twoway),
  'numbers-uifn': productDefaults(NUMBER_PAGES.uifn),
};

// Page id used by each number-type page (NumberTypePage `type` prop).
export const NUMBER_PAGE_IDS = {
  did: 'numbers-did', tollfree: 'numbers-toll-free', mobile: 'numbers-mobile', twoway: 'numbers-2way', uifn: 'numbers-uifn',
};
